import { useCallback, useRef } from 'react';

/**
 * Returns pointer-event handlers that toggle `data-pressed="true"` on the
 * target element while pressed. Works reliably on Android WebView where
 * CSS `:active` is inconsistent.
 *
 * Usage:
 *   const pressHandlers = usePressable();
 *   <Button {...pressHandlers} />
 *
 * Then style with:
 *   &:active, &[data-pressed="true"] { background: #23252a; }
 */
export function usePressable<E extends HTMLElement = HTMLElement>() {
  const ref = useRef<E | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<E>) => {
    const el = e.currentTarget;
    if ((el as unknown as HTMLButtonElement).disabled) return;
    el.setAttribute('data-pressed', 'true');
    ref.current = el;
  }, []);

  const clearPressed = useCallback(() => {
    if (ref.current) {
      ref.current.removeAttribute('data-pressed');
      ref.current = null;
    }
  }, []);

  return {
    onPointerDown,
    onPointerUp: clearPressed,
    onPointerCancel: clearPressed,
    onPointerLeave: clearPressed,
  };
}
